import { useMemo, useState } from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import listStyles from '../../styles/pages/governance/GovernanceListing.module.scss'
import ReportsEnterpriseSection from './ReportsEnterpriseSection'
import AssessmentCoverageCard, { AssessmentScopeContextPanel } from './AssessmentCoverageCard'
import ScannerQualityAssessmentCard from './ScannerQualityAssessmentCard'
import type { ParsedReportPayload } from '../../services/scannerGatewayService'
import { formatScannerSourceLabel } from '../../services/vulnerabilityPostureApi'

type DownloadFormat = 'xml' | 'json' | 'jsonl' | 'pdf' | 'raw' | 'html'

type Props = {
  loading: boolean
  parsed: ParsedReportPayload | null
  onBack: () => void
  onDownload: (format: DownloadFormat) => void
}

const MAIN_TABS = ['Findings', 'Instances', 'Observations', 'Affected Controls', 'Remediation'] as const
type MainTab = (typeof MAIN_TABS)[number]

const SEV_COLOR: Record<string, string> = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#ca8a04',
  LOW: '#2563eb',
  INFO: '#6b7280',
}

/** Safe JSON for Metadata — never throws; skips cycles / non-JSON values / huge blobs. */
function safeJsonStringify(value: unknown, space = 2): string {
  const seen = new WeakSet<object>()
  try {
    return JSON.stringify(
      value,
      (_key, v) => {
        if (typeof v === 'bigint') return String(v)
        if (typeof v === 'function' || typeof v === 'symbol') return undefined
        if (v instanceof Error) return { name: v.name, message: v.message }
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v as object)) return '[Circular]'
          seen.add(v as object)
        }
        return v
      },
      space,
    )
  } catch (err) {
    return JSON.stringify(
      {
        error: 'Failed to serialize metadata',
        message: err instanceof Error ? err.message : String(err),
      },
      null,
      space,
    )
  }
}

/** Lean summary for Metadata — drop heavy aggregation/evidence blobs that crash the view. */
function leanSummaryForMetadata(sum: Record<string, any>) {
  if (!sum || typeof sum !== 'object') return sum
  const {
    aggregation: _aggregation,
    findings: _findings,
    rawFindings: _rawFindings,
    alerts: _alerts,
    matches: _matches,
    ...rest
  } = sum
  const leanAgg = sum.aggregation
    ? {
        uniqueFindings: sum.aggregation.uniqueFindings,
        affectedInstances: sum.aggregation.affectedInstances,
        rawOccurrences: sum.aggregation.rawOccurrences,
        definitionCount: Array.isArray(sum.aggregation.definitions)
          ? sum.aggregation.definitions.length
          : undefined,
        instanceCount: Array.isArray(sum.aggregation.instances)
          ? sum.aggregation.instances.length
          : undefined,
        occurrenceCount: Array.isArray(sum.aggregation.occurrences)
          ? sum.aggregation.occurrences.length
          : undefined,
      }
    : undefined
  return {
    ...rest,
    ...(leanAgg ? { aggregation: leanAgg } : {}),
  }
}

function groupUniqueFindings(findings: Array<Record<string, unknown>>) {
  const map = new Map<
    string,
    { title: string; severity: string; cve: string | null; remediation: string | null; instanceCount: number; hosts: Set<string> }
  >()
  for (const f of findings) {
    const raw = (f.rawFindingJson || {}) as Record<string, unknown>
    const key =
      String(raw.findingDefinitionKey || '') ||
      `${String(f.title || '').toLowerCase()}|${String(f.cve || '').toUpperCase()}|${String(f.cwe || '').toUpperCase()}`
    if (!map.has(key)) {
      map.set(key, {
        title: String(f.title || 'Finding'),
        severity: String(f.severity || 'INFO'),
        cve: f.cve ? String(f.cve) : null,
        remediation: f.remediation ? String(f.remediation) : null,
        instanceCount: 0,
        hosts: new Set(),
      })
    }
    const g = map.get(key)!
    g.instanceCount += 1
    const loc = String(f.url || f.host || '')
    if (loc) g.hosts.add(loc)
  }
  return [...map.values()]
}

function resolveDurationSeconds(scan: ParsedReportPayload['scan'], sum: Record<string, unknown>) {
  if (sum.durationSeconds != null && Number.isFinite(Number(sum.durationSeconds))) {
    return Number(sum.durationSeconds)
  }
  if (scan.startedAt && scan.completedAt) {
    const a = new Date(scan.startedAt).getTime()
    const b = new Date(scan.completedAt).getTime()
    if (Number.isFinite(a) && Number.isFinite(b) && b >= a) {
      return Math.round((b - a) / 1000)
    }
  }
  return null
}

function postureMatchesAssessment(
  currentPosture: Record<string, any> | null,
  counts: { uniqueFindings?: number; affectedInstances?: number },
) {
  if (!currentPosture) return false
  const pu = Number(currentPosture.sameAssetCurrentUniqueFindings ?? 0)
  const pi = Number(currentPosture.sameAssetCurrentInstances ?? 0)
  const au = Number(counts.uniqueFindings ?? 0)
  const ai = Number(counts.affectedInstances ?? 0)
  return pu === au && pi === ai
}

export default function ScannerParsedReportView({ loading, parsed, onBack, onDownload }: Props) {
  const { t } = useI18n()
  const [tab, setTab] = useState<MainTab>('Findings')
  const [historyExpanded, setHistoryExpanded] = useState(false)

  const availableDownloads = useMemo(() => {
    const downloads = parsed?.downloads
    if (!downloads) return [] as Array<{ format: DownloadFormat; label: string }>
    const formats: Array<{ format: DownloadFormat; label: string }> = []
    if (downloads.xml?.available) formats.push({ format: 'xml', label: 'XML' })
    if (downloads.json?.available) formats.push({ format: 'json', label: 'JSON' })
    if (downloads.jsonl?.available) formats.push({ format: 'jsonl', label: 'JSONL' })
    if (downloads.pdf?.available) formats.push({ format: 'pdf', label: 'PDF' })
    if ((downloads as any).html?.available) formats.push({ format: 'html', label: 'HTML' })
    if (downloads.raw?.available) {
      formats.push({ format: 'raw', label: t('reports.downloadRaw', 'Download raw') })
    }
    return formats
  }, [parsed?.downloads, t])

  if (loading || !parsed) {
    return (
      <ReportsEnterpriseSection
        title={t('reports.parsedReportTitle', 'Parsed scanner report')}
        subtitle={t('common.loading', 'Loading...')}
      >
        <Button type="button" size="small" color="outline" onClick={onBack}>
          {t('common.back', 'Back')}
        </Button>
        <p className={entStyles.emptyState}>{loading ? t('common.loading', 'Loading...') : t('common.noData', 'No data')}</p>
      </ReportsEnterpriseSection>
    )
  }

  const { scan, summary, findings, observations, remediation, downloads, parseFailed, parseError } =
    parsed
  const sum = (summary || {}) as Record<string, any>
  const assessmentScope =
    (parsed as any).assessmentScope ||
    (parsed as any).assessment ||
    null
  const assessmentCoverage =
    (parsed as any).assessmentCoverage || sum.assessment || null

  const durationSeconds = resolveDurationSeconds(scan, sum)

  const assessmentForCard = assessmentScope?.counts
    ? {
        ...assessmentScope,
        status: assessmentScope.status || scan.status,
        startedAt: assessmentScope.startedAt || scan.startedAt,
        completedAt: assessmentScope.completedAt || scan.completedAt,
        durationSeconds: assessmentScope.durationSeconds ?? durationSeconds,
        parserStatus: assessmentScope.parserStatus || scan.parserStatus,
        provider: assessmentScope.provider || scan.providerType,
        target: assessmentScope.target || scan.target,
      }
    : assessmentCoverage
      ? {
          ...assessmentCoverage,
          counts: {
            uniqueFindings:
              sum.uniqueFindingsCount ??
              sum.assessmentCounts?.uniqueFindings ??
              (assessmentCoverage as any)?.findings?.total ??
              0,
            affectedInstances:
              sum.affectedInstancesCount ??
              sum.assessmentCounts?.affectedInstances ??
              (assessmentCoverage as any)?.findings?.total ??
              0,
            rawOccurrences:
              sum.rawOccurrencesCount ??
              sum.assessmentCounts?.rawOccurrences ??
              (assessmentCoverage as any)?.findings?.total ??
              0,
            critical: (assessmentCoverage as any)?.findings?.critical ?? sum.criticalCount ?? 0,
            high: (assessmentCoverage as any)?.findings?.high ?? sum.highCount ?? 0,
            medium: (assessmentCoverage as any)?.findings?.medium ?? sum.mediumCount ?? 0,
            low: (assessmentCoverage as any)?.findings?.low ?? sum.lowCount ?? 0,
            info: (assessmentCoverage as any)?.findings?.info ?? sum.infoCount ?? 0,
          },
          coverage: {
            analyzedTargets: (assessmentCoverage as any)?.coverage?.analyzedTargetsCount,
            analyzedHosts: (assessmentCoverage as any)?.coverage?.analyzedHostsCount,
            analyzedUrls: (assessmentCoverage as any)?.coverage?.analyzedUrlsCount,
            requestsSent: (assessmentCoverage as any)?.coverage?.requestsSent,
            pagesCrawled: (assessmentCoverage as any)?.coverage?.pagesCrawled,
          },
          provider: scan.providerType,
          target: scan.target,
          status: scan.status,
          startedAt: scan.startedAt,
          completedAt: (assessmentCoverage as any)?.finishedAt || scan.completedAt,
          durationSeconds,
          parserStatus: scan.parserStatus,
          profile: (assessmentCoverage as any)?.profile || scan.scanType,
        }
      : {
          provider: scan.providerType,
          target: scan.target,
          status: scan.status,
          startedAt: scan.startedAt,
          completedAt: scan.completedAt,
          durationSeconds,
          parserStatus: scan.parserStatus,
          profile: scan.scanType,
          counts: {
            uniqueFindings: sum.uniqueFindingsCount ?? sum.assessmentCounts?.uniqueFindings ?? 0,
            affectedInstances:
              sum.affectedInstancesCount ?? sum.assessmentCounts?.affectedInstances ?? 0,
            rawOccurrences: sum.rawOccurrencesCount ?? sum.assessmentCounts?.rawOccurrences ?? 0,
            critical: sum.criticalCount ?? 0,
            high: sum.highCount ?? 0,
            medium: sum.mediumCount ?? 0,
            low: sum.lowCount ?? 0,
            info: sum.infoCount ?? 0,
          },
        }

  const qualityAssessment =
    sum.qualityAssessment ||
    assessmentScope?.quality ||
    null
  const currentPosture = ((parsed as any).currentPostureContext || null) as Record<string, any> | null
  const historical = ((parsed as any).historicalContext || null) as Record<string, any> | null
  const uniqueFindings = groupUniqueFindings(findings as Array<Record<string, unknown>>)
  const counts = assessmentForCard.counts || {}
  const matchesPosture = postureMatchesAssessment(currentPosture, counts)
  const priorCount = Number(historical?.assessmentCount ?? 0)
  const showHistory = Boolean(historical && priorCount > 0)

  const previousFindings = Number(historical?.previousAssessment?.findingCount ?? 0)
  const currentFindingsCount = Number(counts.uniqueFindings ?? 0)
  const newFindings = Math.max(0, currentFindingsCount - previousFindings)
  const resolvedFindings = Math.max(0, previousFindings - currentFindingsCount)
  const unchangedFindings = Math.min(previousFindings, currentFindingsCount)

  const tabLabels: Record<MainTab, string> = {
    Findings: t('reports.uniqueFindingsTab', 'Findings'),
    Instances: t('reports.instancesTab', 'Instances'),
    Observations: t('reports.observationsTab', 'Observations'),
    'Affected Controls': t('reports.affectedControlsTab', 'Affected Controls'),
    Remediation: t('reports.remediationTab', 'Remediation'),
  }

  const postureSeverityEntries = (['critical', 'high', 'medium', 'low', 'info'] as const)
    .map((k) => ({
      key: k,
      label: t(`severity.${k}`, k),
      value: Number(currentPosture?.severityDistribution?.[k] ?? 0),
    }))
    .filter((row) => row.value > 0)

  return (
    <ReportsEnterpriseSection
      title={t('reports.parsedReportTitle', 'Parsed scanner report')}
      subtitle={String(sum.displayHeadline || t('reports.parsedReportSubtitle', 'Assessment intelligence for this scanner run'))}
      testId="scanner-parsed-report"
    >
      <div className="flex flex-wrap gap-2 mb-4">
        <Button type="button" size="small" color="outline" onClick={onBack}>
          {t('common.back', 'Back to list')}
        </Button>
        {availableDownloads.map((item) => (
          <Button
            key={item.format}
            type="button"
            size="small"
            color="outline"
            onClick={() => onDownload(item.format)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {parseFailed && (
        <div className={listStyles.formError} role="alert">
          {t('reports.parseFailed', 'Scan completed, but report parsing failed.')}
          {parseError ? ` ${parseError}` : null}
        </div>
      )}

      {/* 1. Assessment Summary — single identity / coverage / counts block */}
      <AssessmentCoverageCard assessment={assessmentForCard as any} />

      {/* 2. Scanner Quality — compact, no identity/count duplication */}
      <ScannerQualityAssessmentCard quality={qualityAssessment as any} />

      {/* 3. Current Posture — only when it differs from this assessment (match mode duplicates Summary) */}
      {currentPosture && !matchesPosture ? (
        <section className={entStyles.layer} data-testid="current-posture-context">
          <p className={entStyles.layerLabel}>{t('reports.currentPosture', 'Current Posture')}</p>
          <p className={entStyles.postureStory} style={{ marginBottom: '0.75rem' }}>
            {t(
              'reports.currentPostureDiffersExplain',
              'Current posture differs from this assessment because newer or additional winning assessments contribute to the asset state.',
            )}
          </p>
          <div className={entStyles.posturePanel}>
            <div className={entStyles.postureGrid}>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.openUniqueFindings', 'Open unique findings')}
                </p>
                <p className={entStyles.postureMetricValue}>
                  {currentPosture.sameAssetCurrentUniqueFindings ?? 0}
                </p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.affectedInstances', 'Affected instances')}
                </p>
                <p className={entStyles.postureMetricValue}>
                  {currentPosture.sameAssetCurrentInstances ?? 0}
                </p>
              </div>
              {(currentPosture.activeScannerSources || []).length > 0 ? (
                <div className={entStyles.postureMetric}>
                  <p className={entStyles.postureMetricLabel}>
                    {t('reports.detectedBy', 'Detected by')}
                  </p>
                  <p className={entStyles.postureMetricValue}>
                    {(currentPosture.activeScannerSources || [])
                      .map((s: string) => formatScannerSourceLabel(s))
                      .join(' · ')}
                  </p>
                </div>
              ) : null}
              {currentPosture.asOf ? (
                <div className={entStyles.postureMetric}>
                  <p className={entStyles.postureMetricLabel}>{t('reports.postureAsOf', 'As of')}</p>
                  <p className={entStyles.postureMetricValue}>
                    {new Date(currentPosture.asOf).toLocaleString()}
                  </p>
                </div>
              ) : null}
            </div>
            {postureSeverityEntries.length > 0 ? (
              <>
                <p className={entStyles.layerLabel} style={{ marginTop: '1rem' }}>
                  {t('reports.severity', 'Severity')}
                </p>
                <div className={entStyles.postureGrid}>
                  {postureSeverityEntries.map((row) => (
                    <div key={row.key} className={entStyles.postureMetric}>
                      <p className={entStyles.postureMetricLabel}>{row.label}</p>
                      <p className={entStyles.postureMetricValue}>{row.value}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* 4. Assessment History — only when prior assessments exist */}
      {showHistory ? (
        <section className={entStyles.layer} data-testid="historical-context">
          <p className={entStyles.layerLabel}>{t('reports.assessmentHistory', 'Assessment History')}</p>
          <div className={entStyles.posturePanel}>
            <div className={entStyles.postureGrid}>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.previousAssessment', 'Previous assessment')}
                </p>
                <p className={entStyles.postureMetricValue}>
                  {historical?.previousAssessment?.assessmentAt
                    ? new Date(historical.previousAssessment.assessmentAt).toLocaleDateString()
                    : t('reports.priorAssessmentCount', 'Prior assessments') + `: ${priorCount}`}
                </p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.previousFindings', 'Previous findings')}
                </p>
                <p className={entStyles.postureMetricValue}>{previousFindings}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.currentFindings', 'Current findings')}
                </p>
                <p className={entStyles.postureMetricValue}>{currentFindingsCount}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>{t('reports.newFindings', 'New')}</p>
                <p className={entStyles.postureMetricValue}>{newFindings}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.resolvedFindings', 'Resolved')}
                </p>
                <p className={entStyles.postureMetricValue}>{resolvedFindings}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('reports.unchangedFindings', 'Unchanged')}
                </p>
                <p className={entStyles.postureMetricValue}>{unchangedFindings}</p>
              </div>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <Button
                type="button"
                size="small"
                color="outline"
                onClick={() => setHistoryExpanded((v) => !v)}
              >
                {t('reports.viewHistory', 'View history')}
              </Button>
            </div>
            {historyExpanded && Array.isArray(historical?.trend) && historical.trend.length > 0 ? (
              <ul className="mt-3 space-y-1">
                {historical.trend.map((row: any) => (
                  <li key={row.assessmentId} className={entStyles.postureStory}>
                    {row.assessmentAt ? new Date(row.assessmentAt).toLocaleString() : '—'} ·{' '}
                    {row.providerType} · {row.findingCount}{' '}
                    {t('reports.uniqueFindings', 'Unique findings')}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* 5. Main user-facing tabs */}
      <div className={listStyles.segmentedTabs} role="tablist">
        {MAIN_TABS.map((tabName) => (
          <button
            key={tabName}
            type="button"
            role="tab"
            aria-selected={tab === tabName}
            className={`${listStyles.segmentedTab} ${tab === tabName ? listStyles.segmentedTabActive : ''}`}
            onClick={() => setTab(tabName)}
          >
            {tabLabels[tabName]}
          </button>
        ))}
      </div>

      {tab === 'Findings' && (
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th>{t('reports.vulnerabilityColumn', 'Vulnerability')}</th>
                <th>{t('reports.riskLevel', 'Severity')}</th>
                <th>{t('reports.affectedInstances', 'Affected instances')}</th>
                <th>CVE</th>
                <th>{t('reports.remediation', 'Remediation')}</th>
              </tr>
            </thead>
            <tbody>
              {uniqueFindings.length === 0 ? (
                <tr>
                  <td colSpan={5} className={entStyles.emptyState}>
                    {t('reports.noFindings', 'No findings')}
                  </td>
                </tr>
              ) : (
                uniqueFindings.map((f) => (
                  <tr key={`${f.title}-${f.cve || ''}-${f.severity}`}>
                    <td>{f.title}</td>
                    <td style={{ color: SEV_COLOR[f.severity] || undefined }}>{f.severity}</td>
                    <td>{f.instanceCount}</td>
                    <td>{f.cve || '—'}</td>
                    <td className="text-xs max-w-xs truncate">{f.remediation || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <p className={entStyles.postureStory}>
            {t(
              'reports.uniqueFindingsTabHint',
              'One row per unique finding definition in this assessment.',
            )}
          </p>
        </div>
      )}

      {tab === 'Instances' && (
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th>{t('reports.vulnerabilityColumn', 'Vulnerability')}</th>
                <th>{t('reports.instanceLocation', 'URL / host')}</th>
                <th>{t('reports.riskLevel', 'Severity')}</th>
                <th>{t('reports.statusLabel', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {findings.length === 0 ? (
                <tr>
                  <td colSpan={4} className={entStyles.emptyState}>
                    {t('reports.noFindings', 'No findings')}
                  </td>
                </tr>
              ) : (
                findings.map((f) => (
                  <tr key={`inst-${f.id}`}>
                    <td>{f.title}</td>
                    <td className="font-mono text-xs">{f.url || f.host || '—'}</td>
                    <td style={{ color: SEV_COLOR[f.severity] || undefined }}>{f.severity}</td>
                    <td>{(f as any).status || 'OPEN'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <p className={entStyles.postureStory}>
            {t(
              'reports.instancesTabHint',
              'Each row is an affected instance (URL/host). Unique findings are listed in the Findings tab.',
            )}
          </p>
        </div>
      )}

      {tab === 'Observations' && (
        <div className={listStyles.listStack}>
          {observations.length === 0 ? (
            <p className={entStyles.emptyState}>{t('reports.noObservations', 'No observations')}</p>
          ) : (
            observations.map((o) => (
              <div key={o.id} className={listStyles.listRow}>
                <div className="font-medium">{o.title}</div>
                <div className={listStyles.cellMuted}>{o.recommendation}</div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'Affected Controls' && (
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th>{t('reports.standard', 'Standard')}</th>
                <th>{t('reports.control', 'Control')}</th>
                <th>{t('reports.confidence', 'Confidence')}</th>
              </tr>
            </thead>
            <tbody>
              {findings.flatMap((f) =>
                (f.controlImpacts || []).map((c) => (
                  <tr key={`${f.id}-${c.standardCode}-${c.controlKey}`}>
                    <td>{c.standardCode}</td>
                    <td>{c.controlKey}</td>
                    <td>{c.confidence}</td>
                  </tr>
                )),
              )}
              {findings.every((f) => !f.controlImpacts?.length) && (
                <tr>
                  <td colSpan={3} className={entStyles.emptyState}>
                    {t('reports.noControlImpacts', 'No control impacts mapped')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Remediation' && (
        <div className={listStyles.listStack}>
          {remediation.length === 0 ? (
            <p className={entStyles.emptyState}>
              {findings.length === 0
                ? t('reports.noRemediationZero', 'No remediation required (zero findings).')
                : t('reports.noRemediationYet', 'No remediation candidates yet.')}
            </p>
          ) : (
            remediation.map((r) => (
              <div key={r.id} className={listStyles.listRow}>
                <div className="font-medium">{r.title}</div>
                <ul className="list-disc ml-4 text-sm">
                  {(r.recommendations || []).map((rec: string) => (
                    <li key={rec}>{rec}</li>
                  ))}
                </ul>
                <div className={listStyles.cellMuted}>
                  {t('reports.evidence', 'Evidence')}: {(r.requiredEvidence || []).join(', ')}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* 6. Technical Details — IDs + Metadata only (no count/severity/standards duplication) */}
      <AssessmentScopeContextPanel
        testId="technical-details"
        title={t('reports.technicalDetails', 'Technical Details')}
        subtitle={t('reports.technicalDetailsSubtitle', 'Scan identifiers and metadata.')}
        defaultOpen={false}
      >
        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.scanId', 'Scan ID')}</p>
            <p className={entStyles.postureMetricValue} style={{ fontSize: 12, wordBreak: 'break-all' }}>
              {scan.id}
            </p>
          </div>
          {(parsed as any).assessmentId || assessmentScope?.id ? (
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('reports.assessmentId', 'Assessment ID')}
              </p>
              <p className={entStyles.postureMetricValue} style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {String((parsed as any).assessmentId || assessmentScope?.id)}
              </p>
            </div>
          ) : null}
        </div>

        <p className={entStyles.layerLabel} style={{ marginTop: '1rem' }}>
          {t('reports.metadataTab', 'Metadata')}
        </p>
        <pre className={listStyles.codeBlock} data-testid="parsed-report-metadata">
          {safeJsonStringify({
            scan: {
              id: scan.id,
              providerType: scan.providerType,
              target: scan.target,
              status: scan.status,
              parserStatus: scan.parserStatus,
              scanType: scan.scanType,
              startedAt: scan.startedAt,
              completedAt: scan.completedAt,
              assetId: scan.assetId ?? null,
              informationSystemId: scan.informationSystemId ?? null,
            },
            summary: leanSummaryForMetadata(sum),
            assessment: {
              id: (assessmentForCard as any)?.id ?? null,
              provider: (assessmentForCard as any)?.provider ?? scan.providerType,
              target: (assessmentForCard as any)?.target ?? scan.target,
              status: (assessmentForCard as any)?.status ?? scan.status,
              parserStatus: (assessmentForCard as any)?.parserStatus ?? scan.parserStatus,
              profile: (assessmentForCard as any)?.profile ?? scan.scanType,
              startedAt: (assessmentForCard as any)?.startedAt ?? scan.startedAt,
              completedAt: (assessmentForCard as any)?.completedAt ?? scan.completedAt,
              durationSeconds: (assessmentForCard as any)?.durationSeconds ?? null,
              counts: (assessmentForCard as any)?.counts ?? null,
              coverage: (assessmentForCard as any)?.coverage ?? null,
            },
            assessmentId: (parsed as any).assessmentId || assessmentScope?.id || null,
            downloads,
            qualityAssessment: qualityAssessment
              ? {
                  executionQuality: (qualityAssessment as any).executionQuality ?? null,
                  findingConfidence: (qualityAssessment as any).findingConfidence ?? null,
                  quality: (qualityAssessment as any).quality ?? null,
                  session: (qualityAssessment as any).session ?? null,
                  spider: (qualityAssessment as any).spider ?? null,
                  activeScan: (qualityAssessment as any).activeScan ?? null,
                  liveValidation: (qualityAssessment as any).liveValidation ?? null,
                  falsePositives: (qualityAssessment as any).falsePositives ?? null,
                  staleResources: (qualityAssessment as any).staleResources ?? null,
                  resourcesReturning404: (qualityAssessment as any).resourcesReturning404 ?? null,
                  thisScanActiveFindingCount:
                    (qualityAssessment as any).thisScanActiveFindingCount ?? null,
                  reasons: (qualityAssessment as any).reasons ?? null,
                }
              : null,
          })}
        </pre>
      </AssessmentScopeContextPanel>
    </ReportsEnterpriseSection>
  )
}
